import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import type { DateValidationError } from '@mui/x-date-pickers/models';

interface DatePickerComponentProps {
	label: string;
	value: string | null;
	minDate?: string | Dayjs;
	maxDate?: string | Dayjs;
	onChange: (value: string | null) => void;
	size?: 'small' | 'medium' | undefined;
	testId?: string;
	clearable?: boolean;
}

const DatePickerComponent: React.FC<DatePickerComponentProps> = ({
	label,
	value,
	minDate,
	maxDate,
	onChange,
	size = 'medium',
	testId = undefined,
	clearable = true
}) => {
	const dateValue = value ? dayjs(value) : null;
	const minDayjs = minDate ? dayjs(minDate) : undefined;
	const maxDayjs = maxDate ? dayjs(maxDate) : undefined;

	const [error, setError] = React.useState<DateValidationError | null>(null);

	const errorMessage = React.useMemo(() => {
		switch (error) {
			case 'maxDate':
				return `Please select a date earlier than ${maxDayjs?.format('YYYY-MM-DD')}`;
			case 'minDate': {
				return `Please select a date later than ${minDayjs?.format('YYYY-MM-DD')}`;
			}

			case 'invalidDate': {
				return 'Your date is not valid';
			}

			default: {
				return '';
			}
		}
	}, [error]);

	return (
		<DatePicker
			label={label}
			value={dateValue}
			minDate={minDayjs}
			maxDate={maxDayjs}
			onChange={(newValue) => {
				// Prevent clearing if clearable is false
				if (!clearable && newValue === null) {
					return;
				}
				const isoValue = newValue ? newValue.toISOString().slice(0, 10) : null;
				onChange(isoValue);
			}}
			onError={(newError) => setError(newError)}
			slotProps={{
				textField: {
					helperText: errorMessage,
					size: size,
					inputProps: { 'data-testid': testId }
				},
				field: { clearable: clearable },
			}}
		/>
	);
};

export default DatePickerComponent;
